import {
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleFilled,
  SendOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  EDiscountStatus,
  type EEmailType,
  EMarketingConsentSource,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import AddressDisplay from '../components/address-display';
import { AddressEditDrawer } from '../components/address-edit-drawer';
import CopyToClipboard from '../components/copy-to-clipboard';
import DetailPageHeader from '../components/detail-page-header';
import CustomerCouponModal from '../components/discount/customer-coupon-modal';
import { EmailPreviewDrawer } from '../components/email-preview-drawer';
import Link from '../components/link';
import Table from '../components/table';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import {
  FakturoidInvoiceLink,
  FakturoidSubjectLink,
} from '../fragments/fakturoid-url-component';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  crmCustomerAddressUpdate,
  crmCustomerDetailLoad,
  crmCustomerEmailPreviewClose,
  crmCustomerEmailPreviewLoad,
  crmCustomerEmailResend,
  crmCustomerFakturoidSync,
} from '../reducers/crm-customer-detail/reducer';
import {
  ECustomerCouponSource,
  ECustomerCouponStatus,
  ECustomerEmailDeliveryStatus,
  type ICustomerDetailAddress,
  type ICustomerDetailCoupon,
  type ICustomerDetailCouponUsage,
  type ICustomerDetailEmail,
  type ICustomerDetailEmailProduct,
  type ICustomerDetailOrder,
  type ICustomerDetailSession,
  type ICustomerDetailWatchdog,
  RESENDABLE_EMAIL_TYPES,
} from '../reducers/crm-customer-detail/types';
import { COLOR, FONT_SIZE, SPACING } from '../theme/tokens';

const { Text } = Typography;

const EMAIL_DELIVERY_STATUS_TAG: Record<
  ECustomerEmailDeliveryStatus,
  { color: string; label: string }
> = {
  [ECustomerEmailDeliveryStatus.ACCEPTED]: {
    color: 'green',
    label: 'Accepted',
  },
  [ECustomerEmailDeliveryStatus.REJECTED]: {
    color: 'orange',
    label: 'Rejected',
  },
  [ECustomerEmailDeliveryStatus.FAILED]: { color: 'red', label: 'Failed' },
  [ECustomerEmailDeliveryStatus.UNKNOWN]: {
    color: 'default',
    label: 'Unknown',
  },
};

const getEmailDeliveryStatusTooltip = (row: {
  deliveryStatus: ECustomerEmailDeliveryStatus;
  rejectedRecipients: string[];
  error: string | null;
}): string | undefined => {
  if (row.deliveryStatus === ECustomerEmailDeliveryStatus.FAILED) {
    return row.error ?? undefined;
  }

  if (row.deliveryStatus === ECustomerEmailDeliveryStatus.REJECTED) {
    return `Rejected: ${row.rejectedRecipients.join(', ')}`;
  }

  if (row.deliveryStatus === ECustomerEmailDeliveryStatus.UNKNOWN) {
    return 'No delivery log found';
  }

  return undefined;
};

const ADDRESS_LABEL_STYLE: React.CSSProperties = { width: 100, minWidth: 100 };

const MARKETING_CONSENT_SOURCE_LABEL: Record<string, string> = {
  [EMarketingConsentSource.ACCOUNT_SETTINGS]: 'Account settings',
  [EMarketingConsentSource.CHECKOUT]: 'Checkout',
  [EMarketingConsentSource.ORDERS_CUSTOMER_ASSIGN]: 'Order matching',
};

interface OrderRow extends ICustomerDetailOrder {
  key: string;
}

interface SessionRow extends ICustomerDetailSession {
  key: string;
}

interface WatchdogRow extends ICustomerDetailWatchdog {
  key: string;
}

interface CouponRow extends ICustomerDetailCoupon {
  key: string;
}

interface EmailRow extends ICustomerDetailEmail {
  key: string;
}

const COUPON_STATUS_COLOR: Record<
  EDiscountStatus | ECustomerCouponStatus.USED,
  string
> = {
  [EDiscountStatus.UPCOMING]: 'blue',
  [EDiscountStatus.ACTIVE]: 'green',
  [EDiscountStatus.EXPIRED]: 'default',
  [EDiscountStatus.EXHAUSTED]: 'orange',
  [EDiscountStatus.DISABLED]: 'red',
  [ECustomerCouponStatus.USED]: 'purple',
};

const COUPON_SOURCE_TAG: Record<
  ECustomerCouponSource,
  { color: string; label: string }
> = {
  [ECustomerCouponSource.ASSIGNED]: { color: 'blue', label: 'Assigned' },
  [ECustomerCouponSource.REDEEMED]: { color: 'default', label: 'Public code' },
};

const LEAD_COLORS = [
  'blue',
  'purple',
  'cyan',
  'orange',
  'gold',
  'geekblue',
  'volcano',
];

function CrmCustomerDetailContainer(): React.ReactElement {
  const { customerUUID } = useParams<{ customerUUID: string }>();
  const dispatch = useAppDispatch();

  const customer = useSelector((s: RootState) => s.CrmCustomerDetail.customer);
  const inProcess = useSelector(
    (s: RootState) => s.CrmCustomerDetail.inProcess,
  );
  const fakturoidSyncInProcess = useSelector(
    (s: RootState) => s.CrmCustomerDetail.fakturoidSyncInProcess,
  );
  const emailResendInProcess = useSelector(
    (s: RootState) => s.CrmCustomerDetail.emailResendInProcess,
  );
  const emailPreviewUUID = useSelector(
    (s: RootState) => s.CrmCustomerDetail.emailPreviewUUID,
  );
  const emailPreview = useSelector(
    (s: RootState) => s.CrmCustomerDetail.emailPreview,
  );
  const emailPreviewInProcess = useSelector(
    (s: RootState) => s.CrmCustomerDetail.emailPreviewInProcess,
  );
  const couponCreateInProcess = useSelector(
    (s: RootState) => s.CrmCustomerDetail.couponCreateInProcess,
  );
  const countriesCountries = useSelector(
    (s: RootState) => s.Countries.countries,
  );

  const [editingAddress, setEditingAddress] = useState<
    ICustomerDetailAddress | undefined
  >();
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);

  const loadedUUID = useRef<string | undefined>(undefined);

  useEffect((): void => {
    if (customerUUID && loadedUUID.current !== customerUUID) {
      loadedUUID.current = customerUUID;
      dispatch(crmCustomerDetailLoad(customerUUID));
    }
  }, [dispatch, customerUUID]);

  const [orderColumns, setOrderColumns] = useState<ColumnsType>([]);
  const [orderRows, setOrderRows] = useState<OrderRow[]>([]);
  const [couponColumns, setCouponColumns] = useState<ColumnsType>([]);
  const [couponRows, setCouponRows] = useState<CouponRow[]>([]);
  const [sessionColumns, setSessionColumns] = useState<ColumnsType>([]);
  const [sessionRows, setSessionRows] = useState<SessionRow[]>([]);
  const [watchdogColumns, setWatchdogColumns] = useState<ColumnsType>([]);
  const [watchdogRows, setWatchdogRows] = useState<WatchdogRow[]>([]);
  const [emailColumns, setEmailColumns] = useState<ColumnsType>([]);
  const [emailRows, setEmailRows] = useState<EmailRow[]>([]);

  useEffect((): void => {
    const rows: OrderRow[] = (customer?.orders ?? []).map(
      (o: ICustomerDetailOrder): OrderRow => ({ ...o, key: o.uuid }),
    );

    setOrderColumns([
      {
        title: 'UUID',
        dataIndex: 'uuid',
        render: (_: string, row: OrderRow): React.ReactElement => (
          <Tooltip title={row.uuid}>
            <Link
              to={`/order/${row.uuid}`}
              label={row.uuid.slice(0, 8)}
              openInNewTab
            />
          </Tooltip>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'statusCode',
        sorter: (a: OrderRow, b: OrderRow): number =>
          stringCompare(a.statusCode, b.statusCode),
        filters: [...new Set(rows.map((r) => r.statusCode))].map((c) => ({
          text: c,
          value: c,
        })),
        onFilter: (value, record: OrderRow): boolean =>
          record.statusCode === value,
        render: (code: string): React.ReactElement => <Tag>{code}</Tag>,
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        defaultSortOrder: 'descend',
        sorter: (a: OrderRow, b: OrderRow): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (v: string): React.ReactElement =>
          getTagByRelativeDate({ date: new Date(v) }),
      },
      {
        title: 'Price (without tax)',
        dataIndex: 'price',
        sorter: (a: OrderRow, b: OrderRow): number =>
          (a.price ?? 0) - (b.price ?? 0),
        render: (_: string, row: OrderRow): React.ReactElement => (
          <Space orientation="vertical" size={SPACING.none}>
            {row.price != null && (
              <Text>
                {row.price} {row.currencySymbol || row.currencyCode}
              </Text>
            )}
            {row.priceWithoutTax != null && (
              <Text type="secondary" style={{ fontSize: FONT_SIZE.sm }}>
                {row.priceWithoutTax} {row.currencySymbol || row.currencyCode}{' '}
                excl. VAT
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: 'B2B',
        dataIndex: 'isForCompany',
        filters: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        onFilter: (value, record: OrderRow): boolean =>
          record.isForCompany === value,
        render: (v: boolean): React.ReactElement =>
          v ? <Tag color="blue">B2B</Tag> : <Tag color="default">B2C</Tag>,
      },
      {
        title: 'Language',
        dataIndex: 'leadLanguageCode',
        render: (v: string): string => v || EMPTY_VALUE,
      },
      {
        title: 'Invoice',
        dataIndex: 'invoices',
        render: (_: unknown, row: OrderRow): React.ReactElement => (
          <Space size={SPACING.xs}>
            {row.invoices.length === 0 ? (
              <CloseCircleOutlined style={{ color: COLOR.error }} />
            ) : (
              row.invoices.map((inv) => (
                <FakturoidInvoiceLink
                  key={inv.fakturoidNumber}
                  number={inv.fakturoidNumber}
                  url={inv.publicHtmlUrl}
                  invoiceType={inv.invoiceType}
                />
              ))
            )}
          </Space>
        ),
      },
    ]);

    setOrderRows(rows);
  }, [customer?.orders]);

  useEffect((): void => {
    const rows: SessionRow[] = (customer?.sessions ?? []).map(
      (s: ICustomerDetailSession): SessionRow => ({ ...s, key: s.uuid }),
    );

    const uniqueLeadIds = [...new Set(rows.map((r) => r.leadId))];

    setSessionColumns([
      {
        title: 'Lead',
        dataIndex: 'leadId',
        filters: uniqueLeadIds.map((id) => ({ text: String(id), value: id })),
        onFilter: (value, record: SessionRow): boolean =>
          record.leadId === value,
        render: (leadId: number): React.ReactElement => {
          const color =
            LEAD_COLORS[uniqueLeadIds.indexOf(leadId) % LEAD_COLORS.length];
          return <Tag color={color}>{String(leadId).slice(-6)}</Tag>;
        },
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        defaultSortOrder: 'descend',
        sorter: (a: SessionRow, b: SessionRow): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (v: string): React.ReactElement =>
          getTagByRelativeDate({ date: new Date(v) }),
      },
      {
        title: 'Path',
        dataIndex: 'path',
        ...getColumnSearch('path'),
        render: (v: string): string => v || EMPTY_VALUE,
      },
      {
        title: 'IP',
        dataIndex: 'ipAddress',
        render: (v: string): string => v || EMPTY_VALUE,
      },
      {
        title: 'Bot',
        dataIndex: 'isBot',
        filters: [
          { text: 'Human', value: false },
          { text: 'Bot', value: true },
        ],
        onFilter: (value, record: SessionRow): boolean =>
          record.isBot === value,
        render: (v: boolean): React.ReactElement =>
          v ? <Tag color="red">Bot</Tag> : <Tag color="green">Human</Tag>,
      },
      {
        title: 'Browser',
        dataIndex: 'ua',
        render: (
          ua: { browser?: { name?: string; version?: string } } | undefined,
        ): string =>
          ua?.browser?.name
            ? `${ua.browser.name} ${ua.browser.version ?? ''}`.trim()
            : EMPTY_VALUE,
      },
    ]);

    setSessionRows(rows);
  }, [customer?.sessions]);

  useEffect((): void => {
    const rows: WatchdogRow[] = (customer?.watchdogs ?? []).map(
      (w: ICustomerDetailWatchdog): WatchdogRow => ({ ...w, key: w.uuid }),
    );

    setWatchdogColumns([
      {
        title: 'Product',
        dataIndex: 'productName',
        render: (_: string, row: WatchdogRow): React.ReactElement => (
          <Link
            to={`/product/${row.productUUID}`}
            label={row.productName || row.productUUID}
            openInNewTab
          />
        ),
        ...getColumnSearch('productName'),
      },
      {
        title: 'Language',
        dataIndex: 'languageTypeCode',
        render: (v: string): React.ReactElement => <Tag>{v}</Tag>,
      },
      {
        title: 'Verified',
        dataIndex: 'isVerified',
        render: (v: boolean): React.ReactElement =>
          v ? (
            <Tag color="green">Verified</Tag>
          ) : (
            <Tag color="red">Unverified</Tag>
          ),
      },
      {
        title: 'Registered',
        dataIndex: 'createdAt',
        defaultSortOrder: 'descend',
        sorter: (a: WatchdogRow, b: WatchdogRow): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (v: string): React.ReactElement =>
          getTagByRelativeDate({ date: new Date(v) }),
      },
    ]);

    setWatchdogRows(rows);
  }, [customer?.watchdogs]);

  useEffect((): void => {
    const rows: CouponRow[] = (customer?.coupons ?? []).map(
      (c: ICustomerDetailCoupon): CouponRow => ({ ...c, key: c.uuid }),
    );

    setCouponColumns([
      {
        title: 'Code',
        dataIndex: 'code',
        render: (code: string): React.ReactElement => <Text code>{code}</Text>,
      },
      {
        title: 'Type',
        dataIndex: 'discountTypeCode',
        render: (v: string): React.ReactElement => <Tag>{v}</Tag>,
      },
      {
        title: 'Value',
        dataIndex: 'value',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        render: (description: string | null): React.ReactElement =>
          description ? (
            <Text>{description}</Text>
          ) : (
            <Text type="secondary">{EMPTY_VALUE}</Text>
          ),
      },
      {
        title: 'Source',
        dataIndex: 'source',
        filters: Object.values(ECustomerCouponSource).map(
          (source: ECustomerCouponSource) => ({
            text: COUPON_SOURCE_TAG[source].label,
            value: source,
          }),
        ),
        onFilter: (value, record: CouponRow): boolean =>
          record.source === value,
        render: (source: ECustomerCouponSource): React.ReactElement => (
          <Tag color={COUPON_SOURCE_TAG[source].color}>
            {COUPON_SOURCE_TAG[source].label}
          </Tag>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        filters: [...new Set(rows.map((r) => r.status))].map((s) => ({
          text: s,
          value: s,
        })),
        onFilter: (value, record: CouponRow): boolean =>
          record.status === value,
        render: (
          status: EDiscountStatus | ECustomerCouponStatus.USED,
        ): React.ReactElement => (
          <Tag color={COUPON_STATUS_COLOR[status]}>{status}</Tag>
        ),
      },
      {
        title: 'Valid to',
        dataIndex: 'validTo',
        render: (validTo: Date | null): string =>
          validTo ? dayjs(validTo).format(EDateFormat.DATE) : 'Never expires',
      },
      {
        title: 'Used in',
        dataIndex: 'usages',
        render: (usages: ICustomerDetailCouponUsage[]): React.ReactElement =>
          usages.length === 0 ? (
            <Text type="secondary">{EMPTY_VALUE}</Text>
          ) : (
            <Space orientation="vertical" size={SPACING.none}>
              {usages.map((usage: ICustomerDetailCouponUsage) => (
                <Space key={usage.orderUUID} size={SPACING.xs}>
                  <Tooltip title={usage.orderUUID}>
                    <Link
                      to={`/order/${usage.orderUUID}`}
                      label={usage.orderUUID.slice(0, 8)}
                      openInNewTab
                    />
                  </Tooltip>
                  {getTagByRelativeDate({ date: new Date(usage.usedAt) })}
                </Space>
              ))}
            </Space>
          ),
      },
      {
        title: 'Notified',
        dataIndex: 'notifiedAt',
        render: (
          notifiedAt: string | undefined,
          row: CouponRow,
        ): React.ReactElement => {
          if (row.source === ECustomerCouponSource.REDEEMED) {
            return <Text type="secondary">{EMPTY_VALUE}</Text>;
          }

          return notifiedAt ? (
            getTagByRelativeDate({ date: new Date(notifiedAt) })
          ) : (
            <Tag color="default">Not yet</Tag>
          );
        },
      },
    ]);

    setCouponRows(rows);
  }, [customer?.coupons]);

  useEffect((): void => {
    const rows: EmailRow[] = (customer?.emails ?? []).map(
      (e: ICustomerDetailEmail): EmailRow => ({ ...e, key: e.uuid }),
    );

    setEmailColumns([
      {
        title: 'Sent',
        dataIndex: 'createdAt',
        defaultSortOrder: 'descend',
        sorter: (a: EmailRow, b: EmailRow): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (v: string): React.ReactElement => (
          <Tooltip title={dayjs(v).format(EDateFormat.DATE_TIME)}>
            {getTagByRelativeDate({ date: new Date(v) })}
          </Tooltip>
        ),
      },
      {
        title: 'Email type',
        dataIndex: 'emailTypeName',
        filters: [...new Set(rows.map((r) => r.emailTypeName))].map((n) => ({
          text: n,
          value: n,
        })),
        onFilter: (value, record: EmailRow): boolean =>
          record.emailTypeName === value,
        sorter: (a: EmailRow, b: EmailRow): number =>
          stringCompare(a.emailTypeName, b.emailTypeName),
        render: (_: string, row: EmailRow): React.ReactElement => (
          <Tooltip title={row.emailTypeCode}>
            <Tag>{row.emailTypeName || row.emailTypeCode}</Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Subject',
        dataIndex: 'subject',
        sorter: (a: EmailRow, b: EmailRow): number =>
          stringCompare(a.subject ?? '', b.subject ?? ''),
        render: (subject: string | null): React.ReactElement =>
          subject ? (
            <Text>{subject}</Text>
          ) : (
            <Text type="secondary">{EMPTY_VALUE}</Text>
          ),
      },
      {
        title: 'Status',
        dataIndex: 'deliveryStatus',
        filters: Object.values(ECustomerEmailDeliveryStatus).map(
          (status: ECustomerEmailDeliveryStatus) => ({
            text: EMAIL_DELIVERY_STATUS_TAG[status].label,
            value: status,
          }),
        ),
        onFilter: (value, record: EmailRow): boolean =>
          record.deliveryStatus === value,
        render: (_: string, row: EmailRow): React.ReactElement => (
          <Tooltip title={getEmailDeliveryStatusTooltip(row)}>
            <Tag color={EMAIL_DELIVERY_STATUS_TAG[row.deliveryStatus].color}>
              {EMAIL_DELIVERY_STATUS_TAG[row.deliveryStatus].label}
            </Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Recipients',
        dataIndex: 'recipients',
        render: (recipients: string[]): React.ReactElement => (
          <Space orientation="vertical" size={SPACING.none}>
            {recipients.map((r: string) => (
              <Text key={r}>{r}</Text>
            ))}
          </Space>
        ),
      },
      {
        title: 'Related',
        dataIndex: 'orderUUIDs',
        render: (_: string[], row: EmailRow): React.ReactElement => (
          <Space orientation="vertical" size={SPACING.none}>
            {row.orderUUIDs.map((uuid: string) => (
              <Tooltip key={uuid} title={uuid}>
                <Link
                  to={`/order/${uuid}`}
                  label={`Order ${uuid.slice(0, 8)}`}
                  openInNewTab
                />
              </Tooltip>
            ))}
            {row.products.map((product: ICustomerDetailEmailProduct) => (
              <Tooltip key={product.uuid} title={product.uuid}>
                <Link
                  to={`/product/${product.uuid}`}
                  label={product.name || product.uuid.slice(0, 8)}
                  openInNewTab
                />
              </Tooltip>
            ))}
          </Space>
        ),
      },
      {
        title: 'Opened',
        dataIndex: 'openCount',
        sorter: (a: EmailRow, b: EmailRow): number =>
          numberCompare(a.openCount, b.openCount),
        render: (_: number, row: EmailRow): React.ReactElement => {
          if (row.orderUUIDs.length === 0) {
            return (
              <Tooltip title="Opens are tracked only for order emails">
                <Text type="secondary">{EMPTY_VALUE}</Text>
              </Tooltip>
            );
          }

          if (row.openCount === 0 || !row.firstOpenedAt) {
            return <Tag color="default">Not opened</Tag>;
          }

          return (
            <Tooltip
              title={`First opened ${dayjs(row.firstOpenedAt).format(EDateFormat.DATE_TIME)}`}
            >
              <Tag color="green">Opened ({row.openCount})</Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: string, row: EmailRow): React.ReactElement => {
          const resendableType: EEmailType | undefined =
            RESENDABLE_EMAIL_TYPES.find(
              (t: EEmailType) => t === row.emailTypeCode,
            );
          const orderUUID: string | undefined = row.orderUUIDs[0];

          const previewButton: React.ReactElement = (
            <Tooltip title="Preview email">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={(): void => {
                  dispatch(crmCustomerEmailPreviewLoad(row.uuid));
                }}
              />
            </Tooltip>
          );

          if (!resendableType || !orderUUID || !customerUUID) {
            return previewButton;
          }

          return (
            <Space size={SPACING.xs}>
              {previewButton}
              <Popconfirm
                title="Resend email"
                description={`Send "${row.emailTypeName || row.emailTypeCode}" again for order ${orderUUID.slice(0, 8)}?`}
                onConfirm={(): void => {
                  dispatch(
                    crmCustomerEmailResend(
                      customerUUID,
                      resendableType,
                      orderUUID,
                    ),
                  );
                }}
              >
                <Tooltip title="Resend email">
                  <Button
                    size="small"
                    icon={<SendOutlined />}
                    disabled={emailResendInProcess}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ]);

    setEmailRows(rows);
  }, [customer?.emails, customerUUID, dispatch, emailResendInProcess]);

  const emailTypeCounts: [string, number][] = [
    ...(customer?.emails ?? [])
      .reduce((counts: Map<string, number>, e: ICustomerDetailEmail) => {
        const typeName: string = e.emailTypeName || e.emailTypeCode;
        counts.set(typeName, (counts.get(typeName) ?? 0) + 1);
        return counts;
      }, new Map<string, number>())
      .entries(),
  ].sort((a, b) => b[1] - a[1]);

  if (!customerUUID) {
    return (
      <Typography.Text type="danger">
        No customer UUID provided.
      </Typography.Text>
    );
  }

  if (inProcess || customer === undefined) {
    return <Skeleton active />;
  }

  if (customer === null) {
    return <Typography.Text type="danger">Customer not found.</Typography.Text>;
  }

  const primaryEmail: string | undefined = customer.accounts[0]?.email;
  const lastSeenAt: string | undefined = customer.sessions[0]?.createdAt;

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title={primaryEmail ? `Customer: ${primaryEmail}` : 'Customer'}
        onReload={(): void => {
          dispatch(crmCustomerDetailLoad(customerUUID));
        }}
        isReloadDisabled={inProcess}
        uuid={customer.uuid}
      />

      {customer.deletedAt && (
        <Alert
          type="error"
          showIcon
          title="This customer deleted their own account"
          description={`Deleted on ${dayjs(customer.deletedAt).format(EDateFormat.DATE_TIME_SHORT)}. Their login, saved addresses, and marketing consent were removed. Invoiced orders below are kept as required by law and are no longer linked to an active account.`}
        />
      )}

      <Descriptions
        bordered
        size="small"
        column={2}
        items={[
          {
            key: 'uuid',
            label: 'UUID',
            children: (
              <Space size={SPACING.xs}>
                <Text strong>{customer.uuid}</Text>
                <CopyToClipboard
                  text={customer.uuid}
                  size="small"
                  type="link"
                />
              </Space>
            ),
          },
          {
            key: 'last-seen',
            label: 'Last seen',
            children: lastSeenAt ? (
              getTagByRelativeDate({ date: new Date(lastSeenAt) })
            ) : (
              <Text type="secondary">{EMPTY_VALUE}</Text>
            ),
          },
          {
            key: 'marketing-consent',
            label: 'Marketing consent',
            children: customer.marketingConsent ? (
              <Space size={SPACING.xs}>
                <Tag color="green">Yes</Tag>
                <Text type="secondary">
                  {customer.marketingConsentAt
                    ? `since ${dayjs(customer.marketingConsentAt).format(EDateFormat.DATE)}`
                    : null}
                  {customer.marketingConsentSource
                    ? ` · ${MARKETING_CONSENT_SOURCE_LABEL[customer.marketingConsentSource] ?? customer.marketingConsentSource}`
                    : null}
                </Text>
              </Space>
            ) : (
              <Tag color="default">No</Tag>
            ),
          },
          {
            key: 'fakturoid-id',
            label: 'Fakturoid ID',
            children: (
              <Space size={SPACING.sm}>
                {customer.fakturoidId != null ? (
                  <FakturoidSubjectLink id={customer.fakturoidId} />
                ) : (
                  <Tag color="orange">not synced</Tag>
                )}
                <Tooltip title="Sync customer contact to Fakturoid">
                  <Button
                    size="small"
                    icon={<SyncOutlined spin={fakturoidSyncInProcess} />}
                    loading={fakturoidSyncInProcess}
                    disabled={fakturoidSyncInProcess}
                    onClick={(): void => {
                      dispatch(crmCustomerFakturoidSync(customerUUID));
                    }}
                  >
                    Sync
                  </Button>
                </Tooltip>
              </Space>
            ),
          },
        ]}
      />

      <Collapse
        defaultActiveKey={['accounts', 'addresses', 'orders']}
        items={[
          {
            key: 'accounts',
            label: `Accounts (${customer.accounts.length})`,
            children: (
              <Space orientation="vertical" style={{ width: '100%' }}>
                {customer.accounts.length === 0 ? (
                  <Text type="secondary">No accounts</Text>
                ) : (
                  customer.accounts.map((acc) => (
                    <Descriptions
                      key={acc.uuid}
                      bordered
                      size="small"
                      column={2}
                      styles={{ label: ADDRESS_LABEL_STYLE }}
                      items={[
                        {
                          key: 'email',
                          label: 'Email',
                          children: (
                            <Space size={SPACING.xs}>
                              <Text strong>{acc.email}</Text>
                              <CopyToClipboard
                                text={acc.email}
                                size="small"
                                type="link"
                              />
                            </Space>
                          ),
                        },
                        {
                          key: 'uuid',
                          label: 'UUID',
                          children: (
                            <Space size={SPACING.xs}>
                              <Text code>{acc.uuid}</Text>
                              <CopyToClipboard
                                text={acc.uuid}
                                size="small"
                                type="link"
                              />
                            </Space>
                          ),
                        },
                      ]}
                    />
                  ))
                )}
              </Space>
            ),
          },
          {
            key: 'addresses',
            label: `Addresses (${customer.addresses.length})`,
            children: (
              <Space
                orientation="vertical"
                style={{ width: '100%' }}
                size={SPACING.md}
              >
                {customer.addresses.length === 0 ? (
                  <Text type="secondary">No addresses</Text>
                ) : (
                  [...customer.addresses]
                    .sort((a, b) => {
                      const rank = (x: typeof a): number =>
                        x.addressTypeId === 1
                          ? x.isDefault
                            ? 0
                            : 2
                          : x.isDefault
                            ? 1
                            : 3;
                      return rank(a) - rank(b);
                    })
                    .map((addr) => (
                      <Space
                        key={addr.uuid}
                        orientation="vertical"
                        style={{ width: '100%' }}
                      >
                        <Space size={SPACING.sm}>
                          <Tag
                            color={addr.addressTypeId === 1 ? 'blue' : 'purple'}
                          >
                            {addr.addressTypeId === 1 ? 'Billing' : 'Delivery'}
                          </Tag>
                          {addr.isDefault && <Tag color="green">Default</Tag>}
                          <Tooltip title="Edit address">
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              aria-label="Edit address"
                              onClick={(): void => setEditingAddress(addr)}
                            />
                          </Tooltip>
                        </Space>
                        <AddressDisplay address={addr.address} />
                      </Space>
                    ))
                )}
              </Space>
            ),
          },
          {
            key: 'orders',
            label: `Orders (${customer.orders.length})`,
            children: (
              <Table
                columns={orderColumns}
                dataSource={orderRows}
                isLoading={false}
                scroll={{ x: true }}
              />
            ),
          },
          {
            key: 'leads',
            label: `Leads (${customer.leads.length})`,
            children: (
              <Space orientation="vertical" style={{ width: '100%' }}>
                {customer.leads.length === 0 ? (
                  <Text type="secondary">No leads</Text>
                ) : (
                  customer.leads.map((lead) => (
                    <Space key={lead.uuid} size={SPACING.sm}>
                      <Text code>{lead.uuid}</Text>
                      <CopyToClipboard
                        text={lead.uuid}
                        size="small"
                        type="link"
                      />
                      <Tag>{lead.languageCode}</Tag>
                    </Space>
                  ))
                )}
              </Space>
            ),
          },
          {
            key: 'sessions',
            label: `Sessions (last ${customer.sessions.length})`,
            children: (
              <Table
                columns={sessionColumns}
                dataSource={sessionRows}
                isLoading={false}
                scroll={{ x: true }}
              />
            ),
          },
          {
            key: 'watchdogs',
            label: `Watchdog (${customer.watchdogs.length})`,
            children: (
              <Table
                columns={watchdogColumns}
                dataSource={watchdogRows}
                isLoading={false}
                scroll={{ x: true }}
              />
            ),
          },
          {
            key: 'coupons',
            label: `Coupons (${customer.coupons.length})`,
            children: (
              <Space orientation="vertical" style={{ width: '100%' }}>
                <Button
                  icon={<PlusCircleFilled />}
                  disabled={couponCreateInProcess}
                  onClick={(): void => setIsCouponModalOpen(true)}
                >
                  New coupon for this customer
                </Button>
                <Table
                  columns={couponColumns}
                  dataSource={couponRows}
                  isLoading={couponCreateInProcess}
                  scroll={{ x: true }}
                />
              </Space>
            ),
          },
          {
            key: 'emails',
            label:
              customer.emailsTotal > customer.emails.length
                ? `Emails (latest ${customer.emails.length} of ${customer.emailsTotal})`
                : `Emails (${customer.emails.length})`,
            children: (
              <Space orientation="vertical" style={{ width: '100%' }}>
                <Space size={SPACING.xs} wrap>
                  {emailTypeCounts.map(([typeName, count]) => (
                    <Tag key={typeName}>
                      {typeName} · {count}
                    </Tag>
                  ))}
                </Space>
                <Table
                  columns={emailColumns}
                  dataSource={emailRows}
                  isLoading={emailResendInProcess}
                  scroll={{ x: true }}
                />
              </Space>
            ),
          },
        ]}
      />

      <CustomerCouponModal
        open={isCouponModalOpen}
        onClose={(): void => setIsCouponModalOpen(false)}
        customerUUID={customerUUID}
        customerEmail={primaryEmail}
        inProcess={couponCreateInProcess}
      />

      <EmailPreviewDrawer
        open={emailPreviewUUID !== null}
        inProcess={emailPreviewInProcess}
        preview={emailPreview}
        onClose={(): void => {
          dispatch(crmCustomerEmailPreviewClose());
        }}
      />

      {editingAddress && customerUUID && (
        <AddressEditDrawer
          open={!!editingAddress}
          onClose={(): void => setEditingAddress(undefined)}
          onSave={(address): void => {
            dispatch(
              crmCustomerAddressUpdate(
                customerUUID,
                editingAddress.uuid,
                address,
              ),
            );
          }}
          title={
            editingAddress.addressTypeId === 1
              ? 'Edit billing address'
              : 'Edit delivery address'
          }
          address={editingAddress.address}
          countries={countriesCountries}
          showCompanyFields
          inProcess={inProcess}
        />
      )}
    </Space>
  );
}

export default CrmCustomerDetailContainer;
